import { useMemo } from "react";
import { matchPath, useLocation } from "react-router";

interface Options<T> {
  baseRoute?: string;
  defaultValue?: T;
}

type ConfigItem<T> = [T, string];

/**
 * A custom hook that matches the current pathname against a list of routes.
 * @param config An array of tuples, where each tuple contains a value and a route path. e.g., [["value", "route"], ["value2", "route2"]]
 * @param options Optional configuration, including a `baseRoute` to prepend to all route paths.
 * @returns The value from the first matching configuration entry, or the `defaultValue`.
 */
function useRouteMatch<T>(
  config: ConfigItem<T>[],
  options: Options<T> = {},
): T {
  const { pathname } = useLocation();

  const matchedValue = useMemo(() => {
    for (const [value, route] of config) {
      const path = `${options.baseRoute ?? ""}${route}`;
      if (matchPath(path, pathname)) {
        return value;
      }
    }
    return options.defaultValue ?? config[0][0];
  }, [pathname, config, options.baseRoute, options.defaultValue]);

  return matchedValue;
}

export type { ConfigItem };
export { useRouteMatch };
